// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React, { useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';
import { cityStateLabel } from '../../util/search';
import { haversineMiles } from '../../util/usZip';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  VerifiedHandoffBadge,
  NewListingBadge,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';
import { toggleFavorite, selectIsFavorited } from '../../ducks/favorites.duck';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const HeartIcon = ({ filled }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    // Inline style outranks the global `svg { fill: currentColor }`
    // rule (same trap as the listing-page mosaic heart).
    style={
      filled
        ? { fill: 'var(--rentTheEventPrimary, #CC4733)', stroke: 'var(--rentTheEventPrimary, #CC4733)' }
        : { fill: 'rgba(61, 56, 51, 0.35)', stroke: '#fff' }
    }
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

/**
 * Heart toggle over the card image (audit item 25: hearts existed only
 * on the listing page while the favorites empty state told renters to
 * "browse to find favorites"). Optimistic flip, same duck as the
 * listing page. Hidden for logged-out users and the listing's owner.
 * Lives inside the card's NamedLink, so the click must not navigate.
 */
const FavoriteButtonMaybe = ({ listingId, authorId }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const currentUserId = useSelector(state => state.user?.currentUser?.id?.uuid);
  const serverFavorited = useSelector(state => selectIsFavorited(state, listingId));
  const [optimistic, setOptimistic] = useState(null);
  const isFavorited = optimistic ?? serverFavorited;

  React.useEffect(() => {
    setOptimistic(null);
  }, [serverFavorited]);

  const isOwn = authorId && currentUserId && authorId === currentUserId;
  if (!isAuthenticated || isOwn || !listingId) return null;

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setOptimistic(!isFavorited);
    dispatch(toggleFavorite(listingId))
      .unwrap()
      .catch(() => setOptimistic(null));
  };

  return (
    <button
      type="button"
      className={css.favoriteButton}
      onClick={handleClick}
      aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={isFavorited}
    >
      <HeartIcon filled={isFavorited} />
    </button>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  // Guard: an image entity can sit in the marketplaceData store WITHOUT
  // variants (merged there by a fetch that didn't request fields.image)
  // — Object.keys(undefined) crashed the whole page (white screen on
  // /account/favorites, 2026-07-02). Missing variants → ResponsiveImage
  // renders its visible no-image fallback instead.
  const variants = firstImage?.attributes?.variants
    ? Object.keys(firstImage.attributes.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;
  
  // Check if listing has full verified handoff mode
  const handoffMode = listing?.attributes?.publicData?.handoffMode;
  const showVerifiedBadge = handoffMode === 'full';

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
      {showVerifiedBadge && (
        <VerifiedHandoffBadge />
      )}
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    lazyLoadImage = true,
    // Center of an active location search ({lat,lng}) — when present and
    // the listing has a geolocation, the card shows "x mi away" next to
    // the city (audit "location spine": cards carried no location signal).
    searchCenter = null,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
  } = translations;

  // Check if listing is new (created within last 30 days)
  const isNewListing = () => {
    const createdAt = listing?.attributes?.createdAt;
    if (!createdAt) return false;
    
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return new Date(createdAt) > thirtyDaysAgo;
  };

  // Location line: "Goleta, CA" always (when the listing carries an
  // address), plus "· 9.6 mi away" when a location search is active.
  const cityLabel = cityStateLabel(listing?.attributes?.publicData?.location?.address);
  const geo = listing?.attributes?.geolocation;
  const distanceMiles =
    searchCenter && Number.isFinite(geo?.lat) && Number.isFinite(geo?.lng)
      ? haversineMiles(searchCenter, { lat: geo.lat, lng: geo.lng })
      : null;
  const distanceLabel =
    distanceMiles != null ? `${distanceMiles < 10 ? distanceMiles.toFixed(1) : Math.round(distanceMiles)} mi away` : null;
  const locationLine = [cityLabel, distanceLabel].filter(Boolean).join(' · ');

  // Check for review data (if available in search results)
  const reviewData = listing?.attributes?.metadata?.reviews;
  const hasReviews = reviewData && reviewData.count > 0;
  const shouldShowNewBadge = !hasReviews && isNewListing();

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      <FavoriteButtonMaybe listingId={id} authorId={listing?.author?.id?.uuid} />
      {showListingImage ? (
        <ListingCardImage
          renderSizes={renderSizes}
          title={titlePlain}
          listing={listing}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          aspectRatioClassName={aspectRatioClassName}
          lazyLoadImage={lazyLoadImage}
        />
      ) : (
        <div style={{ position: 'relative' }}>
          <ListingCardThumbnail
            style={cardStyle}
            listingTitle={title}
            className={aspectRatioClassName}
            width={aspectWidth}
            height={aspectHeight}
            setActivePropsMaybe={setActivePropsMaybe}
          />
          {publicData?.handoffMode === 'full' && (
            <VerifiedHandoffBadge />
          )}
        </div>
      )}
      <div className={css.info}>
        {showPrice ? (
          <div className={css.price} title={priceTooltip}>
            {priceMessage}
          </div>
        ) : null}
        {/* Always rendered so the badge's vertical space is reserved —
            cards without a "New" badge stay the same height as those
            with one. */}
        <div className={css.badgeRow}>
          {hasReviews && reviewData ? (
            <span className={css.rating}>
              {intl.formatMessage(
                { id: 'ListingCard.rating' },
                { rating: reviewData.average.toFixed(1), count: reviewData.count }
              )}
            </span>
          ) : shouldShowNewBadge ? (
            <NewListingBadge />
          ) : null}
        </div>
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={classNames(css.title, { [css.lightText]: darkMode })}>
              {titleFormatted}
            </div>
          )}
          {showAuthorInfo ? (
            <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
              {authorName}
            </div>
          ) : null}
          {locationLine ? (
            <div className={classNames(css.locationInfo, { [css.lightText]: darkMode })}>
              {locationLine}
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
